import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { adminApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { EXERCISE_CATEGORIES, HEALTH_TAGS, DIFFICULTIES } from "@/lib/exercises";
import { ImageUpload } from "@/components/ImageUpload";

const linesToList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export const ExerciseEditor = ({ exercise, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: exercise.name,
    image: exercise.image,
    category: exercise.category,
    tags: exercise.tags || [],
    intro: exercise.intro,
    steps: exercise.steps.join("\n"),
    duration: exercise.duration,
    difficulty: exercise.difficulty,
    suitable_for: exercise.suitable_for.join("\n"),
    precautions: exercise.precautions.join("\n"),
    tip: exercise.tip,
    active: exercise.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const save = async () => {
    if (!form.name.trim() || !form.intro.trim()) {
      toast.error("Name and description are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        steps: linesToList(form.steps),
        suitable_for: linesToList(form.suitable_for),
        precautions: linesToList(form.precautions),
      };
      const res = exercise.id ? await adminApi.put(`/exercises/${exercise.id}`, payload) : await adminApi.post("/exercises", payload);
      onSaved(res.data, !exercise.id);
      toast.success(exercise.id ? "Exercise updated" : "Exercise added");
      onClose();
    } catch {
      toast.error("Could not save exercise");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[22rem] max-h-[85vh] overflow-y-auto rounded-3xl p-5" data-testid="exercise-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-emerald-950 dark:text-emerald-50">{exercise.id ? "Edit Exercise" : "Add Exercise"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <Field label="Exercise name" testid="exercise-edit-name" value={form.name} onChange={set("name")} />
          <ImageUpload value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testid="exercise-edit-image" />

          <div className="grid grid-cols-2 gap-2">
            <SelectField label="Category" testid="exercise-edit-category" value={form.category} onChange={set("category")} options={EXERCISE_CATEGORIES} />
            <SelectField label="Difficulty" testid="exercise-edit-difficulty" value={form.difficulty} onChange={set("difficulty")} options={DIFFICULTIES} />
          </div>

          <Field label="Duration / repetitions" testid="exercise-edit-duration" value={form.duration} onChange={set("duration")} />
          <Area label="Short description" testid="exercise-edit-intro" value={form.intro} onChange={set("intro")} rows={3} />
          <Area label="How to perform (one step per line)" testid="exercise-edit-steps" value={form.steps} onChange={set("steps")} rows={5} />
          <Area label="Suitable for (one per line)" testid="exercise-edit-suitable" value={form.suitable_for} onChange={set("suitable_for")} rows={3} />
          <Area label="Safety precautions (one per line)" testid="exercise-edit-precautions" value={form.precautions} onChange={set("precautions")} rows={4} />
          <Area label="Beginner tip" testid="exercise-edit-tip" value={form.tip} onChange={set("tip")} rows={2} />

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Health tags</p>
            <div className="flex flex-wrap gap-1.5">
              {HEALTH_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`exercise-edit-tag-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  onClick={() => toggleTag(t)}
                  className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                    form.tags.includes(t)
                      ? "bg-emerald-700 text-white border-emerald-700"
                      : "bg-white dark:bg-emerald-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-emerald-800"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between py-1">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Visible to users</span>
            <Switch data-testid="exercise-edit-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
          </div>

          <button
            data-testid="exercise-edit-save"
            onClick={save}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 rounded-2xl transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <input data-testid={testid} {...props} className={`${inputCls} mt-1`} />
  </label>
);

const Area = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <textarea data-testid={testid} {...props} className={`${inputCls} mt-1 resize-none`} />
  </label>
);

const SelectField = ({ label, testid, options, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <select data-testid={testid} {...props} className={`${inputCls} mt-1`}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);
